import { FC } from "react"
import { useTranslation } from "react-i18next"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { formatWalletAddress } from "@/helpers/formatWalletAddress"
import { getCurrencyLabel } from "@/helpers/getCurrencyLabel"
import { numberSpaceFixer } from "@/helpers/numberSpaceFixer"
import { CURRENCY } from "@/store/apis/balanceAPI"
import { Button } from "@/ui/Button"
import styles from "./WithdrawSummary.module.scss"
import type { IWithdrawSummaryProps } from "./types"

export const WithdrawSummary: FC<IWithdrawSummaryProps> = ({
  currency,
  balance,
  amount,
  walletAddress,
  commission,
  withdrawPercent,
  limitFrom,
  limitTo,
  isConfigLoading,
  isSubmitting,
  starCoursePrice,
  isStarCourseLoading,
  isStarCourseError,
  onWithdraw,
  onCancel
}) => {
  const { t } = useTranslation()
  const Icon = CurrencyIcon[currency]

  const parsedAmount = Number(amount)
  const youReceive =
    !amount || isNaN(parsedAmount) || parsedAmount <= 0
      ? 0
      : Math.max(parsedAmount * withdrawPercent - commission, 0)

  const isStarCurrency = currency === CURRENCY.STAR

  const usdtValue =
    !isStarCurrency || !starCoursePrice || youReceive <= 0
      ? 0
      : Math.max(
          (parsedAmount * withdrawPercent - commission) / starCoursePrice,
          0
        )

  const isWithdrawDisabled =
    !amount ||
    isNaN(parsedAmount) ||
    parsedAmount === 0 ||
    parsedAmount < limitFrom ||
    parsedAmount > limitTo ||
    parsedAmount > balance ||
    !walletAddress ||
    isConfigLoading ||
    isSubmitting ||
    (isStarCurrency &&
      (isStarCourseLoading || isStarCourseError || !starCoursePrice))

  return (
    <div className={styles.WithdrawSummary}>
      <div className={styles.WithdrawSummary_info}>
        <span className={styles.WithdrawSummary_label}>
          {t("wallet.withdraw.form.totalBalance")}
        </span>
        <div className={styles.WithdrawSummary_balanceRow}>
          {Icon && <Icon />}
          <div className={styles.WithdrawSummary_balanceValue}>
            <span className={styles.WithdrawSummary_balanceAmount}>
              {numberSpaceFixer(balance)}
            </span>
            <span className={styles.WithdrawSummary_balanceUnit}>
              {getCurrencyLabel(t, currency, Math.floor(balance))}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.WithdrawSummary_bottom}>
        <div className={styles.WithdrawSummary_walletRow}>
          <span className={styles.WithdrawSummary_label}>
            {t("wallet.withdraw.form.cryptoWallet")}
          </span>
          <div className={styles.WithdrawSummary_walletChip}>
            {walletAddress ? (
              <span>{formatWalletAddress(walletAddress)}</span>
            ) : (
              <span className={styles.WithdrawSummary_notConnected}>
                {t("wallet.withdraw.form.walletNotConnected")}
              </span>
            )}
          </div>
        </div>

        <div className={styles.WithdrawSummary_receiveBlock}>
          <span className={styles.WithdrawSummary_receiveLabel}>
            {t("wallet.withdraw.form.youReceive")}
          </span>
          <div className={styles.WithdrawSummary_receiveValue}>
            {Icon && <Icon />}
            <span>{numberSpaceFixer(youReceive)}</span>
            {isStarCurrency && (
              <span className={styles.WithdrawSummary_receiveUsdt}>
                {isStarCourseLoading ? (
                  <span className={styles.WithdrawSummary_receiveSkeleton} />
                ) : isStarCourseError || !starCoursePrice ? (
                  t("wallet.withdraw.form.usdtFallback")
                ) : (
                  t("wallet.withdraw.form.usdtEquivalent", {
                    amount: numberSpaceFixer(usdtValue, 2)
                  })
                )}
              </span>
            )}
          </div>
        </div>

        <div className={styles.WithdrawSummary_buttons}>
          <Button
            styles="orange"
            className={styles.WithdrawSummary_withdrawButton}
            disabled={isWithdrawDisabled}
            isLoading={isSubmitting}
            onClick={onWithdraw}
          >
            {t("wallet.withdraw.form.withdrawButton")}
          </Button>
          <Button styles="yellow" onClick={onCancel} disabled={isSubmitting}>
            {t("wallet.withdraw.form.cancelButton")}
          </Button>
        </div>
      </div>
    </div>
  )
}
